import styled from 'styled-components';
import { Typography } from '@material-ui/core';
import { colors, breakpoints, maxBreakpoints } from '../../constants';
import {
  Button,
  CancelButton,
  FormTitle as BaseFormTitle,
  FormLabel as BaseFormLabel,
  FormInput as BaseFormInput,
  StepIndicator as BaseStepIndicator,
  ErrorText as BaseErrorText,
} from '../../constants/base';

// ==============================================
// LAYOUT & CONTAINER COMPONENTS
// ==============================================

const OnboardingBox = styled.div`
  width: 100%;
  min-height: auto;
  margin: 60px 20px;
  padding: 60px 40px 0 40px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 12px ${colors.shadowLight};
  font-family: Mulish;
  position: relative;
  background-color: ${colors.white};

  @media ${maxBreakpoints.tablet} {
    margin: 20px 10px;
    padding: 40px 20px 0 20px;
  }

  @media ${maxBreakpoints.mobileL} {
    margin: 10px 5px;
    padding: 30px 15px 0 15px;
  }
`;

export const OnboardingBoxStep1 = styled(OnboardingBox)`
  max-width: 570px;
`;

export const OnboardingBoxStep2 = styled(OnboardingBox)`
  max-width: 1000px;
`;

export const OnboardingFormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 100%;
`;

export const AddressContainer = styled.div`
  padding: 20px;
  border: 1px solid ${colors.gray09};
  border-radius: 8px;
  background-color: ${(props) =>
    props.hasAddress ? colors.gray12 : 'transparent'};
  box-shadow: ${(props) =>
    props.hasAddress ? `0 2px 8px ${colors.shadowLight}` : 'none'};
  transition: all 0.3s ease;
`;

export const AddressSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const AddressDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media ${maxBreakpoints.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const ZipSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const FormFieldContainer = styled.div`
  margin-top: 16px;
  grid-column: 1 / -1;

  &:first-child {
    margin-top: 0;
  }

  &.last-field {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid ${colors.gray09};
  }
`;

export const InputWithClearContainer = styled.div`
  position: relative;
  width: 100%;

  input {
    padding-right: ${(props) => (props.showClear ? '40px' : '16px')};
  }
`;

// ==============================================
// TYPOGRAPHY & TEXT COMPONENTS
// ==============================================

export const OnboardingTitle = BaseFormTitle;
export const StepIndicator = BaseStepIndicator;
export const FieldLabel = BaseFormLabel;
export const ErrorText = BaseErrorText;
export const FormFieldLabel = styled(BaseFormLabel)`
  font-size: 14px;
  margin-bottom: 6px;
`;

export const ReadOnlyAddressLabel = styled.span`
  font-size: 12px;
  color: ${colors.gray04};
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
  font-family: Mulish;
`;

export const HelperText = styled.p`
  font-size: 12px;
  color: ${colors.gray05};
  font-style: italic;
  margin-top: 6px;
  margin-bottom: 0;
  padding-left: 10px;
  font-family: Mulish;
`;

export const AddressHelperText = styled(HelperText)`
  margin-top: 8px;
  font-size: 12px;
  color: ${colors.gray04};
  font-style: italic;
`;

export const MinPurchaseLabel = styled.span`
  font-size: 14px;
  color: ${colors.gray04};
  white-space: nowrap;
`;

export const CounterLabel = styled.span`
  font-size: 16px;
  color: ${colors.black};
  font-family: Mulish;
  font-weight: 600;
`;

export const CounterHelpText = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${colors.gray04};
  font-family: Mulish;
  line-height: 1.4;
`;

export const FileName = styled.span`
  color: ${colors.gray04};
  font-size: 14px;
`;

export const ZipSuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.gray16};
  font-family: Mulish;
  font-size: 14px;
  color: ${colors.gray02};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.gray11};
  }

  &:last-child {
    border-bottom: none;
  }
`;

// ==============================================
// FORM LAYOUT & GRID SYSTEM
// ==============================================

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 200px;
`;

export const FormGrid = styled.div`
  display: grid;
  gap: 24px;
  width: 100%;
  flex: 1;
  grid-template-columns: 1fr;

  @media ${breakpoints.tablet} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 32px;
  }

  @media ${maxBreakpoints.laptopM} {
    gap: 28px;
  }

  @media ${maxBreakpoints.laptop} {
    gap: 24px;
  }

  @media ${maxBreakpoints.tablet} {
    gap: 20px;
  }
`;

export const FormField = styled.div`
  width: 100%;
  min-width: 0;

  &.full-width {
    width: 100%;
    grid-column: 1 / -1;
  }

  &.wrap-two-columns {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 30px;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.miles-around {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
  }
`;

export const AddressSearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;

  @media ${maxBreakpoints.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

// ==============================================
// INPUT COMPONENTS
// ==============================================

export const StyledInput = BaseFormInput;

export const FormFieldInput = styled(BaseFormInput)`
  width: 100%;
  margin-top: 8px;

  &::placeholder {
    color: ${colors.inputPlaceholderGray};
    font-style: italic;
  }
`;

export const ZipSearchInput = styled(BaseFormInput)`
  width: 100%;

  &::placeholder {
    color: ${colors.inputPlaceholderGray};
  }
`;

export const MinPurchaseInput = styled.input`
  width: 100%;
  min-width: 20px;
  max-width: 60px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 14px;
  text-align: right;
  color: ${(props) =>
    props.disabled || props.readOnly
      ? colors.inputPlaceholderGray
      : colors.inputTextGray};
  cursor: ${(props) => (props.disabled || props.readOnly ? 'pointer' : 'text')};
  flex: 1;

  &.miles-around {
    max-width: 35px;
    text-align: center;
  }

  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
  }

  &:disabled,
  &:read-only {
    cursor: pointer;
  }

  @media ${maxBreakpoints.tablet} {
    font-size: 13px;
    max-width: 50px;
  }

  @media ${maxBreakpoints.mobileL} {
    font-size: 12px;
    max-width: 45px;
  }
`;

export const CounterInput = styled.input`
  background-color: transparent;
  text-align: center;
  border: none;
  font-size: 18px;
  font-weight: 600;
  font-family: Mulish;
  color: ${colors.gray02};

  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    background-color: ${colors.gray10};
    box-shadow: 0 0 0 2px ${colors.inputFocusRedAlpha};
  }
`;

export const FileUploadInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  top: 0;
  left: 0;
`;

// ==============================================
// BUTTON & INTERACTIVE COMPONENTS
// ==============================================

export const BackButton = styled(CancelButton)`
  padding: 12px 24px;
`;

export const NextButton = styled(Button)`
  min-width: 80px;
  padding: 12px 30px;
`;

export const ConfirmEmailButton = styled(Button)`
  margin-top: 0px;
  margin-bottom: 8px;
`;

export const CounterButton = styled(Button)`
  margin-top: 0px !important;
  padding: 0px;
  width: 30px;
  height: 30px;
  border: none;
  background-color: ${colors.red};
  color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  svg {
    width: 12px;
    height: 12px;
    transition: all 0.2s ease;
  }

  &:hover:not(:disabled) {
    background-color: ${colors.red01};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${colors.shadowMedium};

    svg {
      transform: scale(1.1);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px ${colors.shadowLight};

    svg {
      transform: scale(0.95);
    }
  }

  &:disabled {
    background-color: ${colors.gray07};
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 4px ${colors.shadowLight};

    svg {
      opacity: 0.5;
    }
  }
`;

export const ClearAddressButton = styled.button`
  margin-top: 0 !important;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border: none;
  background-color: transparent;
  color: ${colors.gray07};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &::after {
    content: '×';
    display: block;
    line-height: 1;
  }
`;

export const FileUploadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${colors.gray07};
  border-radius: 4px;
  padding: 12px 16px;
  background-color: ${colors.gray11};
  color: ${colors.gray04};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${colors.gray17};
    border-color: ${colors.gray06};
  }
`;

export const FileUploadLabel = styled.span`
  background-color: ${colors.red};
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  margin-right: 12px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
`;

// ==============================================
// FORM CONTROLS (RADIO, CHECKBOX, SELECT)
// ==============================================

export const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;

  @media ${maxBreakpoints.tablet} {
    flex-direction: column;
    gap: 16px;
  }
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: Mulish;
  font-size: 16px;
  color: ${colors.black};

  &:hover {
    color: ${colors.red};
  }
`;

export const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.red};
  cursor: pointer;
`;

export const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.red};
  cursor: pointer;
`;

export const CheckboxLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray02};
  cursor: pointer;
`;

// ==============================================
// DELIVERY & PURCHASE COMPONENTS
// ==============================================

export const DeliveryMethodContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DeliveryMethodRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 5px 16px;
  border: 1px solid ${colors.gray09};
  border-radius: 8px;
  background-color: ${colors.gray12};
  transition: all 0.3s ease;

  &.has-expandable-content {
    flex-direction: column;
    align-items: center;
    gap: 0;
  }
`;

export const DeliveryMethodRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

export const CollapsibleDeliveryAreaSection = styled.div`
  overflow: hidden;
  max-height: ${(props) => (props.isVisible ? '100px' : '0')};
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  margin-top: ${(props) => (props.isVisible ? '12px' : '0')};
  padding: ${(props) => (props.isVisible ? '12px 20px' : '0 16px')};
  transition: all 0.4s ease-in-out;
  transform: ${(props) =>
    props.isVisible ? 'translateY(0)' : 'translateY(-10px)'};
  background-color: ${colors.white};
  border-radius: 6px;
  border: 1px solid ${colors.gray09};
  box-shadow: 0 2px 8px ${colors.shadowLight};

  & > * {
    transition: opacity 0.3s ease
      ${(props) => (props.isVisible ? '0.1s' : '0s')};
    opacity: ${(props) => (props.isVisible ? '1' : '0')};
  }
`;

export const MinPurchaseContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  position: relative;
`;

export const MinPurchaseInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  background-color: ${(props) =>
    props.disabled ? `${colors.gray11}` : `${colors.white}`};
  border: 1px solid
    ${(props) => (props.disabled ? `${colors.gray09}` : `${colors.gray08}`)};
  border-radius: 4px;
  padding: 8px 12px 8px 8px;
  transition: all 0.3s ease;
  min-width: 60px;
  width: 100%;
  max-width: 120px;
  gap: 6px;
  cursor: ${(props) => (props.disabled ? 'pointer' : 'default')};

  &.miles-around {
    min-width: 35px;
    max-width: 50px;
  }

  &:focus-within {
    border-color: ${colors.error03};
    box-shadow: 0 0 0 2px ${colors.inputFocusRedAlpha};
  }

  &:hover:not(.disabled) {
    border-color: ${colors.gray13};
  }

  @media ${maxBreakpoints.tablet} {
    min-width: 50px;
    max-width: 100px;
    padding: 6px 10px 6px 6px;
    gap: 4px;
  }

  @media ${maxBreakpoints.mobileL} {
    min-width: 45px;
    max-width: 90px;
    padding: 4px 8px 4px 4px;
    gap: 3px;
  }
`;

export const DollarSymbol = styled.span`
  font-size: 14px;
  color: ${(props) =>
    props.disabled || props.readOnly
      ? colors.inputPlaceholderGray
      : colors.gray04};
  pointer-events: none;
  font-weight: 500;
  transition: color 0.3s ease;
  user-select: none;
  flex-shrink: 0;
  white-space: nowrap;

  @media ${maxBreakpoints.tablet} {
    font-size: 13px;
  }

  @media ${maxBreakpoints.mobileL} {
    font-size: 12px;
  }
`;

// ==============================================
// ADDITIONAL LOCATIONS & COUNTER COMPONENTS
// ==============================================

export const AdditionalLocationsContainer = styled.div`
  margin-top: 16px;
  padding: 20px;
  border: 1px solid ${colors.gray09};
  border-radius: 8px;
  background-color: ${(props) =>
    props.isActive ? colors.gray12 : 'transparent'};
  box-shadow: ${(props) =>
    props.isActive ? `0 2px 8px ${colors.shadowLight}` : 'none'};
  transition: all 0.3s ease;
`;

export const AdditionalLocationsQuestion = styled.div`
  margin-bottom: 16px;
  font-family: Mulish;
  font-size: 16px;
  color: ${colors.gray02};
`;

export const AdditionalLocationsOptions = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: ${(props) => (props.showCounter ? '20px' : '0')};

  @media ${maxBreakpoints.tablet} {
    flex-direction: column;
    gap: 16px;
  }
`;

export const CounterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0;
  background-color: transparent;
  border: none;
`;

export const CounterControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: nowrap;
  height: 30px;
  width: 123px;
  background-color: ${colors.gray03};
`;

export const CounterHelpLink = styled.a`
  color: ${colors.red};
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.red01};
    text-decoration: underline;
  }
`;

// ==============================================
// FILE UPLOAD COMPONENTS
// ==============================================

export const FileUploadContainer = styled.div`
  position: relative;
  width: 100%;
`;

// ==============================================
// NAVIGATION COMPONENTS
// ==============================================

export const NavigationSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  position: absolute;
  bottom: 50px;
  right: 0;
  z-index: 10;

  @media ${maxBreakpoints.tablet} {
    flex-direction: row;
    gap: 12px;
    justify-content: center;
    align-items: center;
  }
`;

// ==============================================
// ADDRESS & LOCATION COMPONENTS
// ==============================================

export const ReadOnlyAddressField = styled.div`
  padding: 8px 12px;
  background-color: ${colors.gray11};
  border: 1px dashed ${colors.gray07};
  border-radius: 4px;
  font-size: 14px;
  color: ${colors.gray04};
  font-family: Mulish;
  min-height: 20px;
  display: flex;
  align-items: center;
`;

export const ZipSuggestionsList = styled.ul`
  position: absolute;
  top: 80%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${colors.gray07};
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin: 0;
  padding: 0;
  list-style: none;
  box-shadow: 0 4px 8px ${colors.shadowLight};
`;

// ==============================================
// ADDRESS DETAILS TOGGLE COMPONENTS
// ==============================================

export const AddressDetailsToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-top: 16px;
  background-color: ${(props) =>
    props.isExpanded ? `${colors.blue08}` : `${colors.gray12}`};
  border: 1px solid
    ${(props) => (props.isExpanded ? `${colors.blue01}` : `${colors.gray09}`)};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.isExpanded ? `${colors.blue07}` : `${colors.gray10}`};
    border-color: ${(props) =>
      props.isExpanded ? `${colors.blue06}` : `${colors.gray07}`};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px ${colors.shadowLight};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px ${colors.shadowLight};
  }
`;

export const AddressDetailsToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.isExpanded ? `${colors.blue01}` : `${colors.gray04}`};
  font-family: Mulish;
  transition: color 0.3s ease;
`;

export const AddressDetailsToggleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.isExpanded ? `${colors.blue01}` : `${colors.gray06}`};
  color: white;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;
  transform: ${(props) =>
    props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  &::after {
    content: '▼';
    display: block;
    line-height: 1;
  }
`;

export const CollapsibleAddressDetailsSection = styled.div`
  margin-top: 20px;
  padding: 16px;
  background-color: ${colors.gray12};
  border-radius: 6px;
  border: 1px solid ${colors.gray10};
  overflow: hidden;
  max-height: ${(props) => (props.isVisible ? '1000px' : '0')};
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  margin-top: ${(props) => (props.isVisible ? '8px' : '0')};
  padding: ${(props) => (props.isVisible ? '16px' : '0 16px')};
  transition: all 0.4s ease-in-out;
  transform: ${(props) =>
    props.isVisible ? 'translateY(0)' : 'translateY(-10px)'};
  background-color: ${colors.white};
  border: 1px solid ${colors.gray09};
  box-shadow: 0 2px 8px ${colors.shadowLight};

  & > * {
    transition: opacity 0.3s ease
      ${(props) => (props.isVisible ? '0.1s' : '0s')};
    opacity: ${(props) => (props.isVisible ? '1' : '0')};
  }
`;

// ==============================================
// ERROR & VALIDATION COMPONENTS
// ==============================================

export const ValidationAlertIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  color: ${colors.error};
  font-size: 16px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const AddressDetailsToggleLabelContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

// ==============================================
// MODAL COMPONENTS
// ==============================================

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.overlayDark};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
`;

export const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 24px ${colors.shadowDark};
  transform: ${(props) => (props.isVisible ? 'scale(1)' : 'scale(0.9)')};
  transition: transform 0.3s ease;
  font-family: Mulish;
`;

export const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray02};
  margin: 0 0 12px 0;
  font-family: Mulish;
`;

export const ModalMessage = styled.p`
  font-size: 14px;
  color: ${colors.gray04};
  line-height: 1.5;
  margin: 0 0 24px 0;
  font-family: Mulish;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const ModalButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  font-family: Mulish;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;
`;

export const ModalCancelButton = styled(ModalButton)`
  background-color: ${colors.gray12};
  color: ${colors.gray04};
  border: 1px solid ${colors.gray09};

  &:hover {
    background-color: ${colors.gray10};
    border-color: ${colors.gray07};
  }
`;

export const ModalConfirmButton = styled(ModalButton)`
  background-color: ${colors.error};
  color: white;

  &:hover {
    background-color: ${colors.error02};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px ${colors.errorHoverShadowAlpha};
  }

  &:active {
    transform: translateY(0);
  }
`;

// ==============================================
// CONFIRMATION EMAIL COMPONENTS
// ==============================================

export const SuccessMessage = styled(Typography)`
  font-size: 18px;
  font-weight: 500;
  color: ${colors.success};
  margin-bottom: 16px;
  text-align: center;
  font-family: Mulish;
`;

export const InstructionText = styled(Typography)`
  font-size: 16px;
  line-height: 1.6;
  color: ${colors.gray02};
  margin-bottom: 24px;
  text-align: center;
  margin: 0 auto;
  padding: 16px 35px;
  font-family: Mulish;
`;

export const EmailHighlight = styled.span`
  font-weight: 600;
  color: ${colors.blue01};
  background-color: ${colors.blue03};
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  margin: 0 4px;
  font-family: Mulish;
`;

export const EmailIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  background-color: ${colors.blue03};
  border-radius: 50%;
  margin: 20px auto 4px;

  & .MuiSvgIcon-root {
    font-size: 40px;
    color: ${colors.blue01};
  }
`;

export const StepsIndicator = styled.div`
  background-color: ${colors.gray11};
  border-radius: 8px;
  padding: 16px;
  margin: 24px 20px;
  font-size: 14px;
  color: ${colors.gray04};
  line-height: 1.5;
  font-family: Mulish;
  border: 1px solid ${colors.gray09};
`;

export const ActionSection = styled.div`
  text-align: center;
  margin-top: 28px;
  margin-bottom: 24px;
`;

export const FooterSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding-top: 16px;
  border-top: 1px solid ${colors.gray09};
`;

export const FooterLink = styled.a`
  color: ${colors.gray04};
  text-decoration: none;
  font-size: 14px;
  font-family: Mulish;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.blue01};
    text-decoration: underline;
  }
`;

export const HelpText = styled(Typography)`
  font-size: 14px;
  color: ${colors.gray04};
  margin-top: 8px;
  font-family: Mulish;
`;
